"use client";

import { RiFileUploadLine } from "@remixicon/react";
import { HmcMessage } from "@adi-ctx/harmonic-core-components-react";
import { memo, useEffect } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { t } from "@/utils";
import { HmcButton } from "@/components/hmc-on-server";
import { imageToBase64 } from "@/utils";
import Image from "next/image";

type FormImageProps = {
	readonly [key: string]: any;
	readonly hasError: boolean;
	readonly label: string;
	readonly hint?: string;
	readonly field: string;
	readonly placeholder: string;
	readonly isRequired?: boolean;
};

/**
 * Image form field section
 * @constructor
 */
function FormImage(props: FormImageProps) {
	const { control } = useFormContext();
	const { field } = useController({
		control,
		name: props.field,
		rules: { required: Boolean(props.isRequired) },
	});
	const { field: fieldBase64 } = useController({
		control,
		name: `${props.field}Base64`,
	});
	const imageWatch: File | string = useWatch({ control, name: props.field });

	// Prepare image preview
	useEffect(
		() => {
			if (imageWatch) {
				if (typeof imageWatch === "string") {
					fieldBase64.onChange(imageWatch); // save the encoded image too if needed for a preview
				} else {
					imageToBase64(imageWatch, (imageBase64: string) => {
						fieldBase64.onChange(imageBase64); // save the encoded image too if needed for a preview
					});
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[imageWatch]
	);

	return (
		<div className="flex flex-col gap-1">
			<label
				className="hmc-subtitle-s"
				htmlFor="hmc-input-id"
				data-testid="cms-form-image-input-label"
			>
				{props.label}
			</label>
			<div className="flex gap-4 border border-dashed border-gray-200 p-6">
				<div className="flex grow items-center justify-center gap-4">
					<div className="w-[40px] rounded-full bg-blue-100 p-2">
						<RiFileUploadLine data-testid="cms-form-image-input-mask-icon" />
					</div>
					<p
						className="hmc-body-s contentSilent"
						data-testid="cms-form-image-input-mask-text"
					>
						{props.placeholder}
					</p>
					<input
						type="file"
						className="hidden"
						data-testid="cms-form-image-input"
						onChange={(e) => {
							field.onChange(e.target.files?.[0]);
						}}
					/>
					<HmcButton
						kind="tertiary"
						size="xs"
						data-testid="cms-form-image-input-mask"
						onClick={() => {
							const input: HTMLElement = document.querySelector(
								"[data-testid='cms-form-image-input']"
							)!;
							input.click();
						}}
					>
						{t("pages.cms.form-file-button-label")}
					</HmcButton>
				</div>

				{fieldBase64.value && (
					<span className="w-2/3 break-all">
						<span
							className="hmc-overline block bg-gray-200 p-2 font-bold"
							data-testid="cms-form-image-name-preview"
						>
							{typeof imageWatch === "string" ? imageWatch : imageWatch.name}
						</span>
						<Image
							width="0"
							height="0"
							sizes="100vw"
							className="w-full object-cover"
							src={fieldBase64.value}
							alt="image-preview"
							data-testid="cms-form-image-preview"
						/>
					</span>
				)}
			</div>
			{(props.hasError || props.hint) && (
				<HmcMessage
					status={props.hasError ? "error" : props.hint ? "info" : undefined}
					iconName={
						props.hasError ? "error-s" : props.hint ? "info-s" : undefined
					}
					className="block"
					data-testid="cms-form-image-hint"
				>
					{props.hasError
						? t("pages.cms.required-field", {
								field: props.label,
							})
						: props.hint ?? ""}
				</HmcMessage>
			)}
		</div>
	);
}

export default memo(FormImage);
